import { useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';

import useAsyncCancelState from 'global/asyncCancelState';
import { useConfirmModalState } from 'global/confirmModalState';
import { string2trim } from 'function/convert';
import { getErrorMessage } from 'function/message';
import { useLoadingState } from 'global/loadingState';
import useLoginState from 'global/loginState';
import api from 'plugin/api';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import { isEmpty, isObject } from 'utils';
import clean from 'utils/array/clean';
import decode from 'utils/json/decode';

/**
 * [api] 비동기 인터셉터
 * @summary [로딩 상태], [응답 성공여부] 확인
 */

function useAsyncInterceptors() {
  /* router */
  const history = useHistory();

  /* recoil */
  const { setAsyncCancel } = useAsyncCancelState();
  const { setConfirmModal } = useConfirmModalState();
  const { setLoading } = useLoadingState();
  const { resetLogin } = useLoginState();

  /* watch */
  useLayoutEffect(() => {
    // [요청] 인터셉터
    // 값이 문자열인 경우 trim 처리
    // 값이 배열인 경우 빈 항목 삭제
    const requestInterceptors = api.interceptors.request.use(
      config => {
        setLoading(true);

        if (
          process.env.NODE_ENV === 'production' &&
          !config.url.includes(process.env.REACT_APP_API_URL)
        ) {
          config.url = process.env.REACT_APP_API_URL_SFSH.concat(config.url);
        }

        if (!isEmpty(config.data) && isObject(config.data)) {
          config.data = Object.entries(config.data).reduce(
            (result, [key, value]) => ({
              ...result,
              [key]: Array.isArray(value) ? clean(value) : string2trim(value)
            }),
            {}
          );
        }

        config.cancelToken ??= new axios.CancelToken(cancel => {
          setAsyncCancel({ [config.url]: cancel });
        });

        return config;
      },
      error => {
        setLoading(false);

        return Promise.reject(error);
      }
    );

    api.defaults.paramsSerializer = params =>
      Object.entries(params)
        .reduce((result, [key, value]) => {
          result.append(
            key,
            Array.isArray(value)
              ? clean(value)
              : typeof value === 'undefined'
              ? ''
              : string2trim(value)
          );

          return result;
        }, new URLSearchParams())
        .toString();

    // [응답] 인터셉터
    const responseInterceptors = api.interceptors.response.use(
      async ({
        data = {},
        headers = {},
        config: { responseType } = {},
        request: { responseURL } = {}
      } = {}) => {
        setLoading(false);

        switch (responseType) {
          // 파일 다운로드 시
          case 'arraybuffer': {
            const disposition = headers['content-disposition'] ?? '';
            const [, filename = ''] = disposition.split(';');

            let [, name] = filename.split('=');

            if (name) {
              name = decodeURI(name).replaceAll('"', '');
            }

            const response = {
              data: new Uint8Array(data),
              name
            };

            // 파일 유효성 확인
            const text = await new Blob([response.data]).text();
            const json = decode(text, true);

            // 파일이 아닌 경우 에러로 처리
            if (json) {
              return Promise.reject({ error: json.message });
            }

            return response;
          }

          default:
            // 응답 데이터가 객체가 아닌 경우 에러로 처리
            if (!isObject(data)) {
              return Promise.reject({ code: RESPONSE_CODE.Error });
            }

            validLogin(data.code);
        }

        // 서버팀 요청 (TODO: 운영 시 삭제)
        if (data.code === RESPONSE_CODE.Error) {
          console.log(responseURL, data);
        }

        return data;
      },
      ({ response: { data = {} } = {} } = {}) => {
        setLoading(false);
        validLogin(data.code);

        return Promise.reject(data);
      }
    );

    // clean-up [뒷정리 함수] : 컴포넌트 마운트 해제시 동작함
    return () => {
      api.interceptors.request.eject(requestInterceptors);
      api.interceptors.response.eject(responseInterceptors);
    };
  }, []);

  /* method */
  // 로그인 유효성 확인
  const validLogin = code => {
    // 로그인을 하지 않은 경우
    if (
      code === RESPONSE_CODE.Unauthorized &&
      location.pathname !== ROUTER_PATH.login
    ) {
      // 로그아웃 알림 메시지 표시
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(RESPONSE_CODE.Unauthorized)
      });

      const { haccpYn = 'N' } = decode(Cookies.get('haccpYn'), true) ?? {};
      let redirect = '';

      // 배합비
      if (haccpYn === 'N') {
        redirect = `?redirect=${location.pathname}`;
      }

      // 로그아웃
      resetLogin();

      // 로그인 화면 표시
      history.push({
        pathname: ROUTER_PATH.login,
        search: redirect
      });
    }
  };
}

export default useAsyncInterceptors;
